import { ReactNode } from "react";
import { tUserRole, tUserRoleAllowed } from "../types";
import { brand } from "../config/brand";
import {
  Activity,
  AuthorizeLogin,
  AutomationListPage,
  AutomationCreatePage,
  Faqs,
  ResetPassword,
  ChangePassword,
  Insights,
  Organisations,
  OrganisationDetails,
  CreateOrganisation,
  Users,
  UserDetails,
  Policies,
  PolicyDetails,
  Invitations,
  InvitePolicyHolderToSC,
  InviteBrokerToCare,
  InvitationDetails,
  InvitationCancelled,
  InvitationClaimed,
  InvitationExpired,
  Assessment,
  Portfolio,
  Profile,
  Logout,
  Login,
  SignUp,
  CreateQuote,
  CreateClaim,
  ClaimDetails,
  Claims,
  Unverified,
  Toolkits,
  ToolkitEdit,
  Toolkit,
} from "../pages";
import InvitationEmail from "../pages/auth/errors/invitationEmail";
import AlreadyLoggedIn from "../pages/auth/newUser/signUp/alreadyLoggedIn";
import { AutomationDetailsPage } from "../pages/automations/AutomationDetailsPage";
import { HomeRedirect } from "./HomeRedirect";
import { AutomationEditPage } from "../pages/automations/AutomationEditPage";
import LinkSCuserToCare from "../pages/auth/newUser/signUp/linkSCUsertoCare";
import { Role } from "../types/user.type";

/**
 * title: Page Title
 * path: page url path
 * component: The component to render
 * requiredRoles: required roles for a user to access page and appear in sidebar
 *                Admin can see and access everything
 */

const routes: RouteInterface[] = [
  {
    title: "",
    path: "/",
    component: <HomeRedirect />,
    roles: {
      allowed: [
        Role.Unverified,
        Role.Customer,
        Role.Broker,
        Role.Admin,
        Role.SuperAdmin,
      ],
    },
  },
  {
    title: "Insights",
    path: "/risk-and-safety-insights",
    component: <Insights />,
    roles: {
      allowed: [Role.Broker],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Organisation Details",
    path: "/organisations/:orgId",
    component: <OrganisationDetails />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Users",
    path: "/users",
    component: <Users />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "User Details",
    path: "/users/:id",
    component: <UserDetails />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Organisations",
    path: "/organisations",
    component: <Organisations />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Organisations",
    path: "/organisation/create",
    component: <CreateOrganisation />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Policies",
    path: "/policies",
    component: <Policies />,
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Activity",
    path: "/activity",
    component: <Activity />,
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Invitations",
    path: "/invitations",
    component: <Invitations />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Invite broker",
    path: "/invitations/broker/invite",
    component: <InviteBrokerToCare />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: `Invite policy holder to ${brand.parentName}`,
    path: "/invitations/policy-holder/invite",
    component: <InvitePolicyHolderToSC />,
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Invitations",
    path: "/invitations/:id",
    component: <InvitationDetails />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Toolkits",
    path: "/toolkits",
    component: <Toolkits />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Create Toolkit",
    path: "/toolkits/create",
    component: <ToolkitEdit />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Edit Toolkit",
    path: "/toolkits/:id/edit",
    component: <ToolkitEdit />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Toolkit",
    path: "/toolkits/:id",
    component: <Toolkit />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Toolkit",
    path: "/toolkits/code/:code",
    component: <Toolkit />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Policy Details",
    path: "/policies/:policy_id",
    component: <PolicyDetails />,
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Virtual survey",
    path: "/book-assessment",
    component: <Assessment />,
    roles: {
      allowed: [Role.Customer],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Portfolio view",
    path: "/portfolio",
    component: <Portfolio />,
    roles: {
      allowed: [Role.Broker],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "New Quote",
    path: "/quotes/create",
    component: <CreateQuote />,
    pageType: "flow",
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Claims",
    path: "/claims",
    component: <Claims />,
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Create Claim",
    path: "/claims/create",
    component: <CreateClaim />,
    pageType: "flow",
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Claims Details",
    path: "/claims/:claimId",
    component: <ClaimDetails />,
    roles: {
      allowed: [Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "FAQs",
    path: "/faqs",
    component: <Faqs />,
    roles: {
      allowed: [Role.Customer],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Profile",
    path: "/profile",
    component: <Profile />,
    roles: {
      allowed: [Role.Customer, Role.Broker, Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
  },
  {
    title: "Log out",
    path: "/logout",
    component: <Logout />,
  },

  {
    title: Role.Unverified,
    path: "/get-started",
    pathRedirect: {
      unverified: "/get-started",
      customer: "/",
      broker: "/portfolio",
      admin: "/users",
      super_admin: "/users",
    },
    component: <Unverified />,
    roles: {
      allowed: [Role.Unverified],
    },
  },

  {
    title: "Already logged in",
    path: "/already-logged-in",
    component: <AlreadyLoggedIn />,
  },

  //----------- AUTH --------------
  {
    title: "Authorise Login",
    path: "/authorize/login",
    component: <AuthorizeLogin />,
  },
  {
    title: "Login",
    path: "/login",
    component: <Login />,
  },
  {
    title: "Sign up",
    path: "/sign-up",
    component: <SignUp />,
  },
  {
    title: `Link SC user to ${brand.shortName}`,
    path: "/link-sc-user-to-care",
    component: <LinkSCuserToCare />,
  },
  {
    title: "Reset password",
    path: "/password/reset",
    component: <ResetPassword />,
  },
  {
    title: "Change password",
    path: "/password/change",
    component: <ChangePassword />,
  },
  {
    title: "Invitation cancelled",
    path: "/auth/invitation-cancelled",
    component: <InvitationCancelled />,
  },
  {
    title: "Invitation claimed",
    path: "/auth/invitation-claimed",
    component: <InvitationClaimed />,
  },
  {
    title: "Invitation expired",
    path: "/auth/invitation-expired",
    component: <InvitationExpired />,
  },
  {
    title: "Your currently logged",
    path: "/auth/invitation-email",
    component: <InvitationEmail />,
  },
  {
    title: "Automations",
    path: "/automations",
    component: <AutomationListPage />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
    featureFlag: "portal.automations.create",
  },
  {
    title: "Automations",
    path: "/automations/create",
    component: <AutomationCreatePage />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
    featureFlag: "portal.automations.create",
  },
  {
    title: "Automations",
    path: "/automations/:automation_id",
    component: <AutomationDetailsPage />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
    featureFlag: "portal.automations.create",
  },
  {
    title: "Automations",
    path: "/automations/:automation_id/edit",
    component: <AutomationEditPage />,
    roles: {
      allowed: [Role.Admin, Role.SuperAdmin],
      disallowed: [Role.Unverified],
    },
    featureFlag: "portal.automations.create",
  },
];

export interface RouteInterface {
  title: string;
  path: string;
  pathRedirect?: Record<tUserRole, string>;
  component: ReactNode;
  roles?: IUserRolesAccess;
  pageType?: "flow";
  featureFlag?: string;
}

export interface IUserRolesAccess {
  allowed?: tUserRoleAllowed[];
  disallowed?: tUserRoleAllowed[];
}

export default routes;
